import { Provider, useDispatch, useSelector } from 'react-redux';
import './App.css';
import AuthExpirationWatcher from './User/components/AuthExpirationWatcher/AuthExpirationWatcher';
import VerifyEmailAction from './User/components/EmailVerification/VerifyemailAction';
import Login from './User/components/LoginPage/Login';
import ProfilePage from './User/components/ProfilePage/ProfilePage';
import Registration from './User/components/Registration/Registration';
import ForgotPassword from './User/components/ForgotPassword/EmailEntry/ForgotPassword';
import EmailSent from './User/components/ForgotPassword/EmailSent/EmailSent';
import ResetPassword from './User/components/ForgotPassword/ResetPassword/ResetPassword';
import EmailEntry from './User/components/ResendEmail/EmailEntry/EmailEntry';

import BookCheckout from './Library/components/BookCheckout/BookCheckout';
import BookProfilePage from './Library/components/BookProfilePage/BookProfilePage';

import LandingPage from './User/components/LandingPage/LandingPage';
import LandingPageTickets from './Tickets/components/LandingPageTickets/LandingPageTickets';
import LandingPageLibrary from './Library/components/LandingPageLibrary/LandingPageLibrary'

import NavBar from './Middle/components/NavBar/NavBar';
import Footer from './Middle/components/Footer/Footer';
import store from './store';
import { useEffect } from 'react';
import { fetchUser, logout, logoutUser, refreshToken } from './slices/userSlice';
import { Navigate, useLocation, BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import '@fontsource/poppins/400.css';
import '@fontsource/poppins/500.css';
import '@fontsource/poppins/600.css';
import '@fontsource/poppins/700.css';

const theme = createTheme({
  typography: {
    fontFamily: '"Poppins", sans-serif',
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: `
        body {
          font-family: 'Poppins', sans-serif;
        }
      `,
    },
  },
});

function AppContent() {
  const dispatch = useDispatch();
  const location = useLocation();

  const { isAuthenticated, authChecked } = useSelector((state) => state.user);

  const currentPaths = location.pathname.toLowerCase();
  const publicPaths = [
    '/login',
    '/registration',
    '/reset-password',
    '/verify',
    '/check-inbox',
    '/email-verification',
    '/resend-verification'
  ];

  const isPublicPath = publicPaths.some(path =>
    currentPaths.startsWith(path)
  );
  useEffect(() => {
    const initAuth = async () => {
      try {
        const refreshResult = await dispatch(refreshToken()).unwrap();

        if (refreshResult?.token) {
          await dispatch(fetchUser()).unwrap();
        }
      } catch (err) {
        dispatch(logoutUser());
      }
    };

    initAuth();

    const handleStorageChange = (event) => {
      if (event.key === "logout-event") {
        localStorage.removeItem("logout-event");
        dispatch(logout());
      }
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    }
  }, [dispatch]);

  if (!authChecked) {
    return (
      <div className="loading-screen">
        Verifying Session...
      </div>
    );
  }

  if (!isAuthenticated && !isPublicPath) {
    return (
      <Navigate
        to="/login"
        state={{ from: location }}
        replace
      />
    );
  }

  return (
    <>
      <AuthExpirationWatcher />
      <NavBar />
      <AuthExpirationWatcher />
      <ToastContainer
        className="toast-container"
        position="top-right"
        autoClose={3000}
        newestOnTop
        closeOnClick
        pauseOnHover
        draggable
      />

      <div className="App" data-testid="App">
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/Tickets" element={<LandingPageTickets />} />
          <Route path="/registration" element={<Registration />} />
          <Route path="/login" element={<Login timerKey="loginPageResendExpiry" />} />
          <Route path="/ProfilePage" element={<ProfilePage />} />
          <Route path="/email-verification" element={<ForgotPassword timerKey="passwordResetResendExpiry" />} />
          <Route path="/resend-verification" element={<EmailEntry timerKey="emailVerificationResendExpiry" />} />
          <Route path="/check-inbox" element={<EmailSent />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify" element={<VerifyEmailAction />} />
          <Route path="/tickets/:ticketId" element={<LandingPageTickets />} />
          <Route path="/checkout" element={<BookCheckout />} />
          <Route path="/Library" element={<LandingPageLibrary />} />
          <Route path="/book/:id" element={<BookProfilePage />} />
        </Routes>
      </div>

      <Footer />
    </>
  );
}

function App() {
  return (
    <Provider store={store}>
      <ThemeProvider theme={theme}>
        <Router>
          <AppContent />
        </Router>
      </ThemeProvider>
    </Provider>
  );
}

export default App;